import tinycolor from 'tinycolor2';
import {fabric} from 'fabric';
import Sheet from './sheet';
import History from "./history";
import {updateSheetBackgroundImage, updateSheetSize, updateSheetThumbnail} from "../store/reducers/availableSheets";
import {setLoading} from "../store/reducers/modalToggler";
import axios from "axios";
import {URLs} from "./urls";
import {setProgressInNewAssetList, setProgressInNewImageList} from "../store/reducers/userImagesListSlice";
import {setActiveSheetSize} from "../store/reducers/activeSheet";
import {setGradientCol, setOutlineColour, setShadowColour, setSVGColorObject} from "../store/reducers/colorReducer";

const uuid = require("uuid");

export const OPTIONS_TO_ADD = ['id', 'feathering', 'svgStrokeArray','svgColorArray', 'isShadow', 'isOutline', 'isGradient', 'borderColor', 'cornerColor', 'cornerStyle', 'transparentCorners', 'originalSrc', 'actualDim', 'objectCaching'];

export const allowedTypes = [
    'image/png',
    'image/svg',
    'image/jpeg',
    'image/jpg',
    'image/webp',
    'image/svg+xml'
];
export const hexStringToHext = (color) => {
    if (color.length > 1 && color[0] === '#') {
        return color.slice(1);
    }

    return color;
};

export const colorToHex = (color) => {
    return tinycolor(color).toHex();
};

export const getStandardFontSize = (canvas)=>{
    const minimumDimension = Math.min(canvas.width, canvas.height);
    return minimumDimension * 0.4;
}

export const addTextToCanvas = (textValue, textFontSize, name, canvas, isSallyText = false, firstEdit = false) => {
    if(!canvas)return
    let id = uuid.v4();
    let text = new fabric.Textbox(textValue, {
        id: id,
        padding: 0,
        width: isSallyText ? canvas.width-120 : 150,
        left: isSallyText ? 20 : 100,
        top: isSallyText ? 20 : 100,
        fontSize: textFontSize,
        fontWeight: 'normal',
        name: name,
        strokeWidth: 0,
        stroke: '#ff0000',
        borderColor: '#4cc0e7',
        fontFamily: "Open Sans",
        isLockMovement: false,
        isShadow: false,
        isOutline: false,
        textGradient: false,
        textCase: "capitalize",
        fill: '#000000',
        objectCaching: false,
        globalCompositeOperation: "source-over"
    });
    canvas.add(text)
    canvas.setActiveObject(text);
    if(firstEdit) text.scaleToWidth(Math.min(canvas.width, canvas.height)/2);
    canvas.renderAll();
}
export const createThumbnail = async (canvas, sheet) => new Promise((resolve, reject) => {
    let canvasDataURL = '';

    if (!canvas) {
        reject('Invalid canvas instance');
    }
    const toDataURLParams = {enableRetinaScaling: true, quality: 0.7};
    canvasDataURL = canvas.toDataURL(toDataURLParams);
    resolve(canvasDataURL);
});

export const createThumbnailFromJSON = (newSheet, canvas) => {
    return new Promise((resolve, reject) => {
        try {
            updateCanvasBackground(canvas, newSheet);
            canvas._objects = [];

            enlivenOrAddObjects(newSheet.objects, canvas);

            updateCanvasBackgroundImage(canvas, newSheet.backgroundImage);
            canvas.renderAll();

            resizePdfCanvas(newSheet.size.width, newSheet.size.height, canvas)
                .then(() => creatImagesForSvgDownload(newSheet, canvas, resolve, true))
                .catch((error) => reject(error));
        } catch (error) {
            reject(error);
        }
    });
};

const updateCanvasBackground = (canvas, newSheet) => {
    if (newSheet?.backgroundColor) {
        canvas.backgroundColor = newSheet.backgroundColor;
    }
};

const enlivenOrAddObjects = (objects, canvas) => {
    if (objects.length && !(objects[0] instanceof fabric.Object)) {
        fabric.util.enlivenObjects(objects, (enlivenedObjects) => {
            canvas.add(...enlivenedObjects);
        });
    } else {
        canvas._objects = [...objects];
    }
};

const updateCanvasBackgroundImage = (canvas, backgroundImage) => {
    if (backgroundImage) {
        canvas.backgroundImage = backgroundImage;
    } else {
        canvas.backgroundImage = null;
    }
};

export const creatImagesForSvgDownload = (newSheet, canvas, resolve, thumbnails = false) => {
    const image = canvas.toDataURL({
        format: 'png',
        multiplier: thumbnails ? 1.2 : 2,
    });

    const tempCanvas = new fabric.StaticCanvas('c');
    resizePdfCanvas(newSheet.size.width, newSheet.size.height, tempCanvas).then(() => {
        fabric.Image.fromURL(image, (myImg) => {
            const img = myImg.set({ left: 0, top: 0 });
            if (tempCanvas.width >= tempCanvas.height) {
                img.scaleToWidth(tempCanvas.width);
            } else {
                img.scaleToHeight(tempCanvas.height);
            }
            tempCanvas.add(img);
            tempCanvas.renderAll();

            if (thumbnails) {
                const thumbnailImage = tempCanvas.toDataURL();
                tempCanvas.dispose();
                resolve(thumbnailImage);
            } else {
                const svg = tempCanvas.toSVG();
                tempCanvas.dispose();
                resolve({ size: newSheet.size, svg });
            }
        });
    });
};

export const resizePdfCanvas = (sheetWidth, sheetHeight, canvas) => {
    return new Promise((resolve, reject) => {
        try {
            const canvasRatio = sheetWidth / sheetHeight;
            const canvasHeight = sheetHeight;
            const canvasWidth = canvasHeight * canvasRatio;

            canvas.setWidth(canvasWidth);
            canvas.setHeight(canvasHeight);

            const zoom = canvasWidth / sheetWidth;
            canvas.setZoom(zoom);
            canvas.renderAll();

            resolve('');
        } catch (e) {
            console.error('Resize failed:', e);
            reject(e);
        }
    });
};

export const reAddObjects = (objectList, canvas) => {
    objectList.forEach((obj) => {
        obj.addToSource = false;
        obj.generateThumbnail = false;
        canvas.add(obj);
    });
    canvas.renderAll();
};

export const checkObjectListCanvasSourceCorrect = (objectList) => {
    return objectList.every(obj => !obj?.addToSource);
};

const handleGroupDuplicate = (activeObject, canvas) => {
    const fabricObjectList = [...activeObject.group._objects];
    canvas.discardActiveObject().renderAll();
    fabricObjectList.forEach((obj) => {
        obj.clone((clonedObject) => {
            canvas.add(clonedObject);
        }, OPTIONS_TO_ADD);
    });
};

export const duplicateObject = (activeObject, canvas) => {
    let id = uuid.v4();
    if (!activeObject) {
        return;
    }
    if (activeObject.type === 'activeSelection') {
        const objectsToClone = activeObject.getObjects();

        canvas.discardActiveObject();

        objectsToClone.forEach((object) => {
            object.clone((clonedObject) => {
                clonedObject.set({
                    left: object.left + 10,
                    top: object.top + 10,
                    id:id
                });
                canvas.add(clonedObject);
            }, OPTIONS_TO_ADD);
        });
        canvas.requestRenderAll();
    } else {
        activeObject.clone((clonedObject) => {
            clonedObject.set({
                left: activeObject.left + 10,
                top: activeObject.top + 10,
                id:id
            });
            canvas.add(clonedObject);
            canvas.setActiveObject(clonedObject)
        }, OPTIONS_TO_ADD);
    }
};


export const getStoredSheets = () => {
    return new Promise(async (resolve, reject) => {
        try {
            let {templateName, sheets: storedAvailableSheets, thumbnails} = await JSON.parse(localStorage.getItem('atlasmemorial_editor_storage'));
            const storedActiveSheet = await JSON.parse(localStorage.getItem('activeSheet'));

            if (!storedAvailableSheets || !storedActiveSheet) {
                reject('Failed to load the Stored Data from Local Storage', {
                    templateName,
                    storedAvailableSheets,
                    storedActiveSheet
                });
            }
            resolve({templateName, storedAvailableSheets, storedActiveSheet, thumbnails});
        } catch (error) {
            reject(error);
        }
    })
}

export const saveEditor = (templateName='my-temp', availableSheets, activeSheet, canvas) => new Promise((resolve, reject) => {
    try {
        const savedData = [];
        for (const sheet of availableSheets) {
            let data;
            const objs = [];
            if (activeSheet && activeSheet.id === sheet.id) {
                canvas?.getObjects()?.forEach((e) => objs.push(JSON.stringify(e.toJSON(OPTIONS_TO_ADD))));
                data = {
                    ...sheet,
                    history: undefined,
                    thumbnail: '',
                    objects: [...objs],
                    currentWidth:canvas.getWidth(),
                    currentHeight:canvas.getHeight()
                };
                localStorage.setItem('activeSheet', JSON.stringify(data));
            } else {
                if (activeSheet) {
                    sheet.objects.forEach((e) => objs.push(JSON.stringify(e.toJSON(OPTIONS_TO_ADD))));
                } else sheet.objects.forEach((e) => objs.push(JSON.stringify(e)));
                data = {
                    ...sheet,
                    history: undefined,
                    thumbnail: '',
                    objects: [...objs],
                    currentWidth:canvas.getWidth(),
                    currentHeight:canvas.getHeight()
                };
            }

            savedData.push(data);

            if (availableSheets.length === savedData.length) {

                if (!activeSheet) {
                    localStorage.setItem('activeSheet', JSON.stringify(savedData[0]));
                }

                localStorage.setItem('atlasmemorial_editor_storage', JSON.stringify({
                    templateName,
                    sheets: savedData,
                }));
                
                resolve({
                    canvasData: JSON.stringify({
                        templateName,
                        sheets: savedData,
                    }),
                });
            }
        }
    } catch (err) {
        reject(err);
    }
});


export const enlivenFabricObjectList = (list) => new Promise((resolve) => {
    fabric.util.enlivenObjects(list, (enlivenedList) => {
        resolve(enlivenedList);
    });
});

export const initializeFeaturingAndExtractFonts = (objectList, sheet) => {
    const fontList = {};

    objectList.forEach((object) => {
        if(object.type === 'image' && object.feathering){
            applyFeathering(object, object?.canvas)
        }
        if(['textbox', 'text', 'itext'].includes(object.type)){
            const objectFont = object.fontFamily;
            fontList[objectFont] = objectFont;
        }
        object.addToSource = true;
    });
    console.log('fontList', fontList)
    return {
        fabricObjectList: [...objectList],
        fontList,
    };
}

export const getFabricInstances = async (objectList, sheet) => new Promise((resolve) => {
    const {size, zoom} = sheet;
    const {height, width} = size;

    const tempCanvas = new fabric.Canvas();

    tempCanvas.setBackgroundColor('white');
    tempCanvas.setZoom(zoom);
    tempCanvas.setHeight(height * zoom);
    tempCanvas.setWidth(width * zoom);
    tempCanvas.renderAll();

    objectList.map((object) => {
        if(object.type === 'image' && object.feathering){
            applyFeathering(object, tempCanvas)
        }
        object.addToSource = true;
        tempCanvas.add(object);
        tempCanvas.renderAll();
    });

    resolve({
        fabricObjectList: [...tempCanvas.getObjects()],
        canvasInstance: tempCanvas,
    });
});



export const initSheets = async (storedAvailableSheets, storedActiveSheet, thumbnails = []) => {
    let newSheetList = [];
    let sheetBg = false
    return new Promise(async (resolve, reject) => {

        if (!storedActiveSheet || !storedAvailableSheets) {
            reject('There might be an issue with the data params being passed to the loadSheets function: ', {
                storedAvailableSheets,
                storedActiveSheet
            })
        }

        for (const sheet of storedAvailableSheets) {
            const newSheet = new Sheet(sheet.size, {isStaringSheet: false}, 1.0, sheet.sheetTemplate, sheet.thumbnail, sheet.quantity);
            newSheet.size = sheet.size;
            const objArr = [];
            sheet.objects.forEach((e) => objArr.push(JSON.parse(e)));
            if (sheet.id !== storedActiveSheet.id) {
                const enlivenedList = await enlivenFabricObjectList(objArr);
                const {fabricObjectList} = await getFabricInstances(enlivenedList, sheet);
                newSheet.objects = [...fabricObjectList];
            } else {
                newSheet.objects = [...objArr];
            }

            newSheet.id = sheet.id;
            newSheet.setSheetHistory(new History());
            newSheetList.push(newSheet);
            if(sheet.backgroundImage)sheetBg = sheet.backgroundImage
            if (newSheetList.length === storedAvailableSheets.length) {
                let resSheet = newSheetList.find(sheet => sheet.id === storedActiveSheet.id);
                resSheet.size = sheet.size
                resolve({newSheet: resSheet, newSheetList, sheetBg})
            }
        }
    })
}

export const URLMaker = (url) => {
    if(!url) return ''
    return `${process.env.REACT_APP_cdnURL}/` + url
}

export const URLMakerForThumbnails = (url) => {
    if(!url) return ''
    return `${process.env.REACT_APP_OnFlyResize}/fit-in/100x100/` + url
}

export const getFabricImageFromURL = (path, callback) => {
    const image = new Image();
    image.crossOrigin = 'Anonymous';
    image.onload = () => {
        const fabricImage = new fabric.Image(image);
        callback(fabricImage);
    };
    fetch(
        path,
        {
            method: 'GET',
            mode: 'cors',
            referrerPolicy: 'strict-origin-when-cross-origin',
            headers: {
                Origin: window.location.origin,
            },
        },
    ).then((res) => {
        image.src = res.url;
    }).catch((error) => {
        console.error(error);
    });
};


export const modifiedTemplateList = (data, activeSheet) => new Promise((resolve, reject) => {
    if (!data || !activeSheet) {
        reject('Missing data params for modifiedGraphicsList function: ', {data, activeSheet});
    }

    const newTemplateList = [];
    data.forEach((template) => {
        if (template.FabricJs.sheets) {
            newTemplateList.push({
                id: template.id,
                cover: template.coverpic,
                canvasData: template.FabricJs.sheets[0],
                title: template.Title,
                tags: template.metadata,
                category: template.categories,
                price: template.price,
                thumbnails: template.MetaData?.thumbnailInfo?.coverPic
            });
            resolve(newTemplateList);
        }
    });
});

export const modifiedImagesList = (data, activeSheet) => new Promise((resolve, reject) => {
    if (!data || !activeSheet) {
        reject('Missing data params for modifiedGraphicsList function: ', {data, activeSheet});
    }

    const newImagesList = [];
    data.forEach((images) => {
        if (images.image) {
            newImagesList.push({
                id: images.id,
                imageUrl: images.image,
                thumbnail: images.thumbnail,
                tags: images.Tags,
                compressedUrl: images.ObjectMetaData?.Compressed?.isCompressed ? images.ObjectMetaData?.Compressed?.CompressedUrl : ''
            });
            resolve(newImagesList);
        }

    });
});

export const getMaxLineWidth = (textObject) => {
    const textLineArray = textObject.__lineWidths;
    let maxWidth = 0;
    textLineArray.forEach((lineWidth) => {
        if (lineWidth > maxWidth) {
            maxWidth = lineWidth;
        }
    });
    return maxWidth;
};
export const setCanvasBackground = (canvas, background, dispatch, activeSheet) => {
    if(typeof(background) === 'object')background = background.src

    let zoom = canvas.getZoom()
    fabric.Image.fromURL(background, function (Image) {
        const scaleX = canvas.width / (Image.width * zoom);
        const scaleY = canvas.height / (Image.height * zoom);

        const scale = Math.max(scaleX, scaleY);

        canvas.setBackgroundImage(Image, canvas.renderAll.bind(canvas), {
            scaleX: scale,
            scaleY: scale,
            top: (canvas.height / 2) / zoom,
            left: (canvas.width / 2) / zoom,
            originX: 'center',
            originY: 'center'
        });
        dispatch(updateSheetBackgroundImage({
            sheetId: activeSheet.id,
            backgroundImage: canvas.backgroundImage,
        }))
        createThumbnail(canvas).then((thumbnail) => dispatch(updateSheetThumbnail({
            sheetId: window.activeSheetId,
            thumbnail,
        })));
        dispatch(setLoading(false));
    }, {crossOrigin: 'anonymous'});
};

export const roundedCorners = (ctx,rx,ry) => {
    return new fabric.Rect({
        name: 'Curved',
        left: -ctx.width / 2,
        top: -ctx.height / 2,
        absolutePositioned: false,
        rx: rx / ctx.scaleX,
        ry: ry / ctx.scaleY,
        width: ctx.width,
        height: ctx.height,
        fill: '#000000'
    })
}

export const initializeScale = (obj, cp) => {
    var elementToDraw = obj._element,
        elWidth = elementToDraw.naturalWidth || elementToDraw.width,
        elHeight = elementToDraw.naturalHeight || elementToDraw.height;
    obj.width = elWidth;
    obj.height = elHeight;

    let imageWidth = obj.getScaledWidth();
    let imageHeight = obj.getScaledHeight();
    let clipPathWidth = cp.getScaledWidth();
    let clipPathHeight = cp.getScaledHeight();

    let newScaleX = imageWidth / clipPathWidth;
    let newScaleY = imageHeight / clipPathHeight;

    let tempScaleX = newScaleX / obj.scaleX;
    let tempScaleY = newScaleY / obj.scaleY;

    cp.scaleX = tempScaleX;
    cp.scaleY = tempScaleY;

    cp.absolutePositioned = false;
    cp.left = -obj.width / 2;
    cp.top = -obj.height / 2;
};

export const fileUploaderToServer = (files, id, dispatch) => new Promise((resolve, reject) => {
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
        formData.append('image', files[i]);
        formData.append('imageType', 'Not-applicable');
    }

    const token = sessionStorage.getItem('auth_token');

    axios.post(URLs.UPLOAD_FILES, formData, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
        },
        onUploadProgress(progressEvent) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            dispatch(setProgressInNewImageList({id, progress: percentCompleted}));
        },
    })
        .then((res) => {
            if (res.status === 201) {
                console.log('response', res.data)
                resolve(res.data);
            }
        })
        .catch((err) => {
            reject(err);
        });
});

export const assetsUploaderToServer = (files, id, dispatch, type) => new Promise((resolve, reject) => {
    const formData = new FormData();

    for (let i = 0; i < files.length; i++) {
        formData.append('image', files[i]);
    }
    formData.append('asset_type',type);

    const token = sessionStorage.getItem('auth_token');

    axios.post(URLs.ADD_GLOBAL_ASSET, formData, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
        },
        onUploadProgress(progressEvent) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            dispatch(setProgressInNewAssetList({type, id, progress: percentCompleted}));
        },
    })
        .then((res) => {
            if (res.status === 201) {
                console.log('response', res.data)
                resolve(res.data);
            }
        })
        .catch((err) => {
            reject(err);
        });
});

export const createMultipleObjects = (number, uploaderCount) => {
    const objects = [];
    for (let i = 0; i < number; i++) {
        const newImage = {
            imageUrl: '',
            thumbnail: '',
            uploadID: uploaderCount,
            progress: 0,
            loading: true,
        };
        objects.push(newImage);
    }
    return objects;
};

export const testFileTypes = (files) => {
    let isAllowed = false;
    files.forEach((file) => {
        isAllowed = testIndividualFileType(file);
    });

    return isAllowed;
};
const testIndividualFileType = (file) => {
    if (allowedTypes.indexOf(file.type) === -1) {
        return false;
    }
    return true;
};
export const horizontalFlip = (activeObject, canvas) => {
    if (!activeObject) return;
    activeObject.flipX = !activeObject.flipX;
    canvas.renderAll();
    canvas.fire('object:modified', {target: activeObject});
};
export const verticalFlip = (activeObject, canvas) => {
    if (!activeObject) return;
    activeObject.flipY = !activeObject.flipY;
    canvas.renderAll();
    canvas.fire('object:modified', {target: activeObject});
};

export const GetCanvasAtResolution = (newWidth, newHeight, canvas, dispatch, resizeCanvas, activeSheet) => {
    if (!canvas.originalSize) {
        canvas.originalSize = {
            width: newWidth,
            height: newHeight,
        };
    }


    activeSheet.updateSheetSize({ width: newWidth, height: newHeight });

    const backgroundImage = canvas.backgroundImage;

    // Update redux
    const tempSheet = { ...activeSheet };
        tempSheet.size = {height:newHeight,width:newWidth};

    dispatch(
        updateSheetSize({
            sheetId: activeSheet.id,
            width: newWidth,
            height: newHeight,
        })
    );
    window.isResized = true;
    dispatch(setActiveSheetSize({ size: tempSheet.size }));
        setTimeout(() => {
            if (backgroundImage) {
                setCanvasBackground(
                    canvas,
                    backgroundImage.getSrc(),
                    dispatch,
                    activeSheet
                );
            }
            createThumbnail(canvas).then((thumbnail) => dispatch(updateSheetThumbnail({
                sheetId: window.activeSheetId,
                thumbnail,
            })));
        }, 10);
};

export function fitGroupInsideCanvas(canvas) {
    const objects = canvas.getObjects();
    const group = new fabric.Group(objects);
    objects.forEach(obj => canvas.remove(obj)); // Remove old objects
    canvas.add(group);
    const canvasWidth = canvas.getWidth();
    const canvasHeight = canvas.getHeight();

    const groupBounds = group.getBoundingRect(); // true = absolute coords


    if (groupBounds.width > canvasWidth || groupBounds.height > canvasHeight) {

        const scaleX = canvasWidth / groupBounds.width;
        const scaleY = canvasHeight / groupBounds.height;
        const scale = Math.min(scaleX, scaleY);

        group.scale(scale);

        group.setCoords();
        group.set({left: 0, top: 0});
        canvas.requestRenderAll();
    }

    const items = group._objects;
    group._restoreObjectsState();
    canvas.remove(group);
    items.forEach(item => {
        canvas.add(item);
        item.setCoords();
    });
    window.isResized = false;
        canvas.discardActiveObject();
        canvas.renderAll();


}

export const changeEffectColor = (color, accordion, activeObject, canvas, dispatch, activeSetting) => {
    if (activeObject && activeObject.type === 'textbox') {
        switch (accordion) {
            case 'shadow' :
                if (activeObject.shadow) {
                    activeObject.shadow.color = color
                    dispatch(setShadowColour(color))
                    canvas.renderAll()
                }
                break;
            case 'outline':
                if (activeObject) {
                    activeObject.set({
                        stroke: color,
                    });
                    dispatch(setOutlineColour(color))
                    canvas.renderAll()
                }
                break;
            case 'gradient':
                if (activeObject) {
                    if(typeof (activeObject.fill) === 'object'){
                        if(isColorCodeValid(color)){
                            let tmpFill = activeObject.fill;
                            tmpFill.colorStops[activeSetting.index].color = color
                            activeObject.set({
                                dirty:true,
                                fill:tmpFill,
                                textGradient:true
                            })
                            dispatch(setGradientCol(color))
                        }
                    }
                    else{
                        activeObject.set({
                            fill: color,
                        });
                    }
                    canvas.renderAll();
                }
                break;
            default:
                break
        }
    }
}

function isColorCodeValid(colorCode) {
    colorCode = colorCode.replace(/^#/, '');
    return /^([0-9a-fA-F]{3}){1,2}$/.test(colorCode);
}

export const getRelativePath = (url) => {
    if (!isURL(url)) {
        return url;
    }
    let dummyAnchor = document.createElement('a');
    dummyAnchor.href = url;

    let path = dummyAnchor.pathname + dummyAnchor.search + dummyAnchor.hash;
    return path.replace(/^\//, '');
}

function isURL(url) {
    let urlPattern = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i;
    return urlPattern.test(url);
}


export const logIfStoredDataExists = () => {
    let temp1 = localStorage.getItem('atlasmemorial_editor_storage');
    if (temp1) {
        console.log('%c[✓] Sheet List Data Found', 'color:green');
    } else {
        console.log('%c[X] Sheet List Data not Found', 'color:red');
    }
    let temp2 = localStorage.getItem('activeSheet');
    if (temp2) {
        console.log('%c[✓] Active Sheet Data Found', 'color:green');
    } else {
        console.log('%c[X] Active Sheet Data not Found', 'color:red');
    }
}

export const isURLOk = (str) => {
    return str.startsWith('http://') || str.startsWith('https://');
}

export function dataURLtoFile(dataURL, filename) {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
}

export const applyFeathering = (activeObject, canvas) => {
    var imageWidth = activeObject.width, imageHeight = activeObject.height;

    var demoCanvas = fabric.util.createCanvasElement();
    // Set canvas size
    demoCanvas.width = imageWidth;
    demoCanvas.height = imageHeight;

    var demoCtx = demoCanvas.getContext("2d");
    var base_image = new Image();
    base_image.src = '/edgeEffects/rectangleEdges.png';
    base_image.onload = function() {

        // Draw the scaled image on the canvas
        demoCtx.drawImage(base_image, 0, 0, imageWidth, imageHeight);

        // Get the image data
        var imgData = demoCtx.getImageData(0, 0, imageWidth, imageHeight);
        var pixels = imgData.data;

        // Loop through pixel data
        let edgeTransparentPixels = [];
        for (var i = 0; i < pixels.length; i += 4) {
            var alphaIndex = pixels[i + 3];
            // Check if the pixel is transparent
            if (alphaIndex < 255) {
                // Calculate x and y coordinates of the transparent pixel
                var x = (i / 4) % imageWidth;
                var y = Math.floor((i / 4) / imageWidth);
                edgeTransparentPixels.push({x: x, y: y, alpha: alphaIndex});
            }
        }
        activeObject.filters.push(new fabric.Image.filters.Feathering4({"edgeTransparentPixels": edgeTransparentPixels}));
        activeObject.set('feathering', true)
        activeObject.applyFilters();
        canvas?.renderAll();
    }
}

export const getIconColors = (activeObject, preset = 'fill') => {
    let svgColorArray = [];
    if (activeObject.type === 'path') {
        if (activeObject[preset])
            svgColorArray.push({id: activeObject.id, value: activeObject[preset]})
    }
    if (activeObject.type === 'group') {
        let objects = activeObject._objects;
        svgColorArray = [];
        objects.forEach((object, index) => {
            if (object[preset])
                svgColorArray.push({id: index, value: object[preset],freeze: false})
        })
    }
    return svgColorArray;
}

export const changeSVGColor = (color, svgColorObj, canvas, dispatch, activeObject) => {
    if (!canvas) return;
    if (!activeObject) return;

    if (activeObject.type === 'path') {
        activeObject.set({fill: color});
    }
    if (activeObject.type === 'group' && activeObject.custom.type === 'svg') {
        activeObject.set({dirty: true});
        activeObject._objects[svgColorObj.id].set({fill: color})
        activeObject.svgColorArray = activeObject.svgColorArray.map((colorObj, index) =>
            index === svgColorObj.id ? { ...colorObj, value: color } : colorObj
        );
        let val = {
            ...svgColorObj,
            value:color
        }
        dispatch(setSVGColorObject(val))
    }
    canvas.renderAll();
}

export const getImageTypeFromURL = (url) => {
    const extension = url.split('.').pop().toLowerCase();
    switch (extension) {
        case 'jpg':
        case 'jpeg':
            return 'image/jpeg';
        case 'png':
            return 'image/png';
        case 'gif':
            return 'image/gif';
        case 'svg':
            return 'image/svg+xml';
        case 'webp':
            return 'image/webp';
        default:
            return 'unknown';
    }
}